import { endOfTomorrow, format, isAfter, isTomorrow } from 'date-fns';
import { FlattenedOrderStatuses } from '@sg/graphql-schema';
import { OrderType } from '@sg/graphql-schema';

import { getStartAndEndDates } from '@order/utils';

import { type useHomeBannerContent } from '../../hooks';
import { type useHomeNavigation } from '../useHomeNavigation';

// ─── Home Banners ───────────────────────────────────────────────────────────

export const getLoggedOutXsBanner = (
  bannerContent: ReturnType<typeof useHomeBannerContent>,
  navigationOptions: ReturnType<typeof useHomeNavigation>,
) => {
  return {
    message: bannerContent.bannerSignUpMessage,
    cta: bannerContent.bannerSignUpCta,
    onPress: navigationOptions.navigateToSignIn,
  };
};

export const getPendingFeedbackBanner = (
  bannerContent: ReturnType<typeof useHomeBannerContent>,
  navigationOptions: ReturnType<typeof useHomeNavigation>,
) => {
  return {
    message: bannerContent.bannerRateMessage,
    cta: bannerContent.bannerRateCta,
    onPress: navigationOptions.navigateToRateOrder,
  };
};

export const getLateOrderBanner = (
  bannerContent: ReturnType<typeof useHomeBannerContent>,
  navigationOptions: ReturnType<typeof useHomeNavigation>,
) => {
  return {
    ...getSharedOrderBannerInfo(bannerContent, navigationOptions),
    message: bannerContent.bannerOrderLateMessage,
  };
};

export const getOrderNotTrackableBanner = (
  order: BannerOrder,
  bannerContent: ReturnType<typeof useHomeBannerContent>,
  navigationOptions: ReturnType<typeof useHomeNavigation>,
) => {
  const timeToInterpolate = getTimeToInterpolate(order);

  return {
    ...getSharedOrderBannerInfo(bannerContent, navigationOptions),
    message: bannerContent.bannerOrderNotTrackableMessage.replaceAll(
      '#eta',
      timeToInterpolate,
    ),
  };
};

export const getActiveOrderBanner = (
  order: BannerOrder,
  bannerContent: ReturnType<typeof useHomeBannerContent>,
  navigationOptions: ReturnType<typeof useHomeNavigation>,
  shouldBreakLine: boolean,
) => {
  if (order.orderStatus === FlattenedOrderStatuses.Failed) {
    return {
      ...getSharedOrderBannerInfo(bannerContent, navigationOptions),
      message: bannerContent.bannerOrderCancelledMessage,
    };
  }

  const { bannerTomorrow, bannerEta, bannerEtaOnDate } = bannerContent;

  const timeToInterpolate = getTimeToInterpolate(order);
  const dateToInterpolate = format(order.orderTime, 'M/d');

  const isOrderTomorrow = isTomorrow(order.orderTime);
  const isOrderAfterTomorrow = isAfter(order.orderTime, endOfTomorrow());
  const isFutureOrder = isOrderTomorrow || isOrderAfterTomorrow;

  const tomorrow = bannerTomorrow;
  const date = isOrderTomorrow ? tomorrow : dateToInterpolate;
  const eta = bannerEta.replaceAll('#eta', timeToInterpolate);
  const etaOnDate = bannerEtaOnDate
    .replaceAll('#eta', timeToInterpolate)
    .replaceAll('#date', date);

  const messageToInterpolate = getBannerMessageForStatus(
    bannerContent,
    order.orderStatus,
  );

  const futureDivider = shouldBreakLine ? ` \n` : ' • ';

  const message = isFutureOrder
    ? messageToInterpolate + futureDivider + etaOnDate
    : eta + ' ' + messageToInterpolate;

  return {
    ...getSharedOrderBannerInfo(bannerContent, navigationOptions),
    message,
  };
};

export const getPromoBanner = (
  bannerPromoMessage: string,
  bannerPromoCta: string,
  bannerPromoDeepLink: string,
  navigateWithDeeplink: () => void,
) => {
  return {
    message: bannerPromoMessage,
    cta: bannerPromoCta,
    deepLink: bannerPromoDeepLink,
    onPress: navigateWithDeeplink,
  };
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const getSharedOrderBannerInfo = (
  bannerContent: ReturnType<typeof useHomeBannerContent>,
  navigationOptions: ReturnType<typeof useHomeNavigation>,
) => {
  return {
    cta: bannerContent.bannerOrderCta,
    onPress: navigationOptions.navigateToOrderStatus,
  };
};

const getTimeToInterpolate = (order: BannerOrder) => {
  const { orderTime, orderType } = order;

  return orderType === OrderType.Delivery
    ? getFormattedOrderRange(orderTime)
    : format(orderTime, 'h:mmaaa').toLowerCase();
};

/**
 * If the start and end times are in the same period.
 * We should omit the first time period.
 * For example: 12:50am - 1:00am becomes 12:50 - 1:00am
 */
const getFormattedOrderRange = (orderTime: Date) => {
  const { startDate, endDate } = getStartAndEndDates(orderTime);

  const startPeriod = format(startDate, 'a');
  const endPeriod = format(endDate, 'a');

  const startTimeWithoutPeriod = format(startDate, 'h:mm').toLowerCase();
  const startTime = format(startDate, 'h:mmaaa').toLowerCase();
  const endTime = format(endDate, 'h:mmaaa').toLowerCase();

  return startPeriod === endPeriod
    ? `${startTimeWithoutPeriod} - ${endTime}`
    : `${startTime} - ${endTime}`;
};

const getBannerMessageForStatus = (
  bannerContent: ReturnType<typeof useHomeBannerContent>,
  orderStatus: FlattenedOrderStatuses,
) => {
  switch (orderStatus) {
    case FlattenedOrderStatuses.Received: {
      return bannerContent.bannerOrderReceivedMessage;
    }

    case FlattenedOrderStatuses.Preparing: {
      return bannerContent.bannerOrderPreparingMessage;
    }

    case FlattenedOrderStatuses.ReadyForCourier: {
      return bannerContent.bannerOrderReadyForCourierMessage;
    }

    case FlattenedOrderStatuses.Delivering: {
      return bannerContent.bannerOrderDeliveringMessage;
    }

    case FlattenedOrderStatuses.Completed: {
      return bannerContent.bannerOrderCompletedMessage;
    }

    default: {
      return bannerContent.bannerOrderCancelledMessage;
    }
  }
};

// ─── Types ──────────────────────────────────────────────────────────────────

export type BannerOrder = {
  orderTime: Date;
  startTime: string;
  endTime: string;
  orderType: OrderType;
  orderStatus: FlattenedOrderStatuses;
};
